/**
 * Social share buttons for a Battle Mode result.
 * Pure web intent URLs — no API integrations needed.
 *
 * Renders a Twitter/X, LinkedIn, and Copy-link button row. Pre-formats the
 * share copy with verdict + scores so the click → publish flow is 1-tap.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TwitterLogo, LinkedinLogo, LinkSimple, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';


const buildShareText = (t, { verdict, primaryScore, competitorScore, primaryUrl, competitorUrl }) => {
  const stripScheme = (u = '') => u.replace(/https?:\/\//, '').replace(/^www\./, '');
  const me = stripScheme(primaryUrl);
  const them = stripScheme(competitorUrl);
  const vars = { me, them, primary: primaryScore, competitor: competitorScore };
  if (verdict === 'primary') return t('share.msg_win', vars);
  if (verdict === 'competitor') return t('share.msg_lose', vars);
  return t('share.msg_tie', vars);
};


export default function ShareButtons({ shareUrl, ...battle }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const text = buildShareText(t, battle);
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(shareUrl);

  const twitterHref = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}&hashtags=FunnelHack,AI`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t('share.link_copied'));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('share.copy_fail'));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-buttons">
      <a
        href={twitterHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-twitter-btn"
        className="px-3 py-2 bg-black hover:bg-[#1A1A1A] text-white text-xs font-semibold rounded-lg flex items-center gap-2 transition-all border border-white/15"
      >
        <TwitterLogo size={14} weight="fill" />
        {t('share.post_x')}
      </a>
      <a
        href={linkedinHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-linkedin-btn"
        className="px-3 py-2 bg-[#0A66C2] hover:bg-[#0958A8] text-white text-xs font-semibold rounded-lg flex items-center gap-2 transition-all"
      >
        <LinkedinLogo size={14} weight="fill" />
        LinkedIn
      </a>
      <button
        onClick={copy}
        data-testid="share-copy-btn"
        className="px-3 py-2 bg-white/10 hover:bg-white/15 text-white text-xs font-semibold rounded-lg flex items-center gap-2 transition-all border border-white/10"
      >
        {copied ? <Check size={14} weight="bold" className="text-[#10B981]" /> : <LinkSimple size={14} weight="bold" />}
        {copied ? t('share.copied') : t('share.copy_link')}
      </button>
    </div>
  );
}
